import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Box, Loader, Menu, Popover, Drawer, Group } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useLocation } from "react-router-dom";
import swal from "sweetalert";
import { useSelector } from "react-redux";
import AppNavbar from "../../../components/AppNavbar/AppNavbar";
import schedulerService from "../../../services/scheduler";
import moment from "moment";
import { getAllAttendenceStaff, fetchAttendanceRange, updateAttendenceStaff, deleteAttendanceStaff } from "../../../services/staff";
import TimeOffPanel from "./TimeOffPanel";
import RequestTimeOffDrawer from "./RequestTimeOffDrawer";
import StaffTimeEntryDrawer from "./StaffTimeEntryDrawer";
import TimeEntryDetailDrawer from "./TimeEntryDetailDrawer";
import TimeEntryEditDrawer from "./TimeEntryEditDrawer";
import WeekPicker from "./WeekPicker";
import WeekCalendar from "./WeekCalendar";
import ShiftDrawer from "./ShiftDrawer";
import ShiftPopup from "./ShiftPopup";
import Icon from "./icons";
import css from "./Scheduler.module.css";
import {
  startOfWeek, addDays, isoDate, weekLabel, startOfDay, sameDay,
  fmtRange, hrsBetween, DOW, MON_SHORT, entryMeta,
} from "./scheduleUtils";

const idOf = (o) => (typeof o === "object" ? o?._id || o?.id : o);
// A clocked-out entry that hasn't been submitted is a draft; otherwise show its
// approval status. Records predating the `submitted` field are grandfathered as
// already submitted. Only drafts and rejected entries can be (re)submitted.
const tcStatus = (t) => (t?.submitted === false ? "draft" : (t?.approvedStatus || "pending"));
// Anything not yet approved can be submitted / resubmitted for approval.
const isSubmittable = (t) => t?.approvedStatus !== "approved";

export default function SchedulerStaff() {
  const { profile } = useSelector((s) => s?.profile || {});
  const { attendanceStaff } = useSelector((s) => s?.staff || {});
  const [detailRecord, setDetailRecord] = useState(null);
  const [tab, setTab] = useState("timecard"); // 'timecard' | 'schedule'
  const [schedView, setSchedView] = useState("calendar"); // 'list' | 'calendar'
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [hover, setHover] = useState(null);
  const [addOpen, { open: openAdd, close: closeAdd }] = useDisclosure(false);
  const [sel, setSel] = useState(() => new Set());
  const [submitting, setSubmitting] = useState(false);
  const [datePop, setDatePop] = useState(false);
  const [dpMonth, setDpMonth] = useState(() => { const d = new Date(weekStart); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const goToDate = (d) => {
    if (!d) return;
    setWeekStart(startOfWeek(d));
    setDpMonth(new Date(d.getFullYear(), d.getMonth(), 1));
    setDatePop(false);
  };

  const myId = profile?.user?._id || profile?.user?.id;

  // Deep-link from a Scheduler notification: jump to the shift's week on the
  // Schedule tab, then consume the nav state so refresh doesn't re-trigger.
  const location = useLocation();
  useEffect(() => {
    // Open the Schedule tab directly when navigated here with that intent
    // (e.g. the "View schedule" link on the dashboard calendar).
    if (location.state?.tab === "schedule") {
      setTab("schedule");
      window.history.replaceState({}, "");
      return;
    }
    const focusShiftId = location.state?.focusShiftId;
    if (!focusShiftId) return;
    schedulerService.getShift(focusShiftId).then((shift) => {
      if (shift?.date) {
        setWeekStart(startOfWeek(new Date(shift.date)));
        setTab("schedule");
      }
    }).catch(() => {});
    window.history.replaceState({}, "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const data = await schedulerService.getShifts({ from: isoDate(weekStart), to: isoDate(addDays(weekStart, 6)) });
    setShifts(data || []);
    const range = [moment(weekStart), moment(addDays(weekStart, 6)).endOf("day")];
    if (myId) await getAllAttendenceStaff(1, range, "", myId);
    setLoading(false);
  }, [weekStart, myId]);

  useEffect(() => {
    load();
  }, [load]);

  // Overtime config from Scheduler settings (threshold hours + alerts toggle).
  const [otSettings, setOtSettings] = useState(null);
  // Count of my pending time-off requests, for the Submitted tile.
  const [timeOffPending, setTimeOffPending] = useState(0);
  // The staff's own approved/pending time off, shown on their schedule calendar.
  const [myTimeOff, setMyTimeOff] = useState([]);
  // Submitted tab has a sub-tab: 'submitted' (timesheets) | 'timeoff' (PTO).
  const [subTab, setSubTab] = useState("submitted");
  // Reusable "Request Time Off" side panel (openable from the Time Off tab or a
  // shift's detail drawer, prefilled with the shift date).
  const [reqOpen, setReqOpen] = useState(false);
  const [reqPrefill, setReqPrefill] = useState("");
  const [reqReload, setReqReload] = useState(0);
  const openRequest = (dateISO = "") => { setReqPrefill(dateISO || ""); setReqOpen(true); };
  useEffect(() => {
    schedulerService.getTimeOff().then((list) => {
      const arr = list || [];
      setTimeOffPending(arr.filter((r) => r.status === "pending").length);
      setMyTimeOff(arr.filter((r) => r.status === "approved" || r.status === "pending"));
    }).catch(() => {});
  }, [tab, reqReload]);
  useEffect(() => {
    schedulerService.getSchedulerSettings().then((s) => setOtSettings(s || null)).catch(() => {});
  }, []);
  // Whether staff may manually add time entries (Scheduler setting; default on).
  const allowManualEntry = otSettings?.rules?.allowManualEntry !== false;

  // ── Submitted tab: weekly aggregates over a multi-week window (separate fetch
  // so it doesn't clobber the per-week Time Card data in redux). ──
  const [submittedWeeks, setSubmittedWeeks] = useState([]);
  const [submittedLoading, setSubmittedLoading] = useState(false);
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [submittedRefresh, setSubmittedRefresh] = useState(0);
  const [editRecord, setEditRecord] = useState(null);
  useEffect(() => {
    if (tab !== "submitted" || !myId) return undefined;
    let cancelled = false;
    setSubmittedLoading(true);
    const range = [moment().subtract(11, "weeks").startOf("week"), moment().endOf("week")];
    fetchAttendanceRange(range, myId).then((arr) => {
      if (cancelled) return;
      const submitted = (arr || []).filter((t) => t.submitted !== false);
      const byWeek = {};
      submitted.forEach((t) => {
        const d = t.date?.date || t.startTime;
        if (!d) return;
        const wkStart = startOfWeek(new Date(d));
        const key = moment(wkStart).format("YYYY-MM-DD");
        (byWeek[key] = byWeek[key] || { weekStart: wkStart, records: [] }).records.push(t);
      });
      const weeks = Object.values(byWeek).map((w) => {
        const totalHours = w.records.reduce((a, t) => a + (Number(t.total_work_time) || 0) / 3600, 0);
        const anyRejected = w.records.some((t) => t.approvedStatus === "rejected");
        const allApproved = w.records.length > 0 && w.records.every((t) => t.approvedStatus === "approved");
        const status = anyRejected ? "rejected" : allApproved ? "approved" : "pending";
        const rejectionReason = (w.records.find((t) => t.approvedStatus === "rejected" && t.rejectionReason) || {}).rejectionReason;
        return { ...w, totalHours, count: w.records.length, status, rejectionReason };
      }).sort((a, b) => b.weekStart - a.weekStart);
      setSubmittedWeeks(weeks);
      // Keep an open side panel in sync after an edit/resubmit re-fetch.
      setSelectedWeek((prev) => {
        if (!prev) return prev;
        const k = moment(prev.weekStart).format("YYYY-MM-DD");
        return weeks.find((w) => moment(w.weekStart).format("YYYY-MM-DD") === k) || null;
      });
      setSubmittedLoading(false);
    }).catch(() => { if (!cancelled) { setSubmittedWeeks([]); setSubmittedLoading(false); } });
    return () => { cancelled = true; };
  }, [tab, myId, submittedRefresh]);

  // Resubmit the entire weekly time card: send every not-yet-approved entry
  // (rejected, pending or draft) back to the administrator for approval.
  // Resubmission is week-level — never per individual entry.
  const resubmitWeek = async (wk) => {
    const payload = (wk.records || []).filter((t) => t.approvedStatus !== "approved").map((t) => ({ id: idOf(t), submitted: true, approvedStatus: "pending" }));
    if (!payload.length) return;
    setSubmitting(true);
    await updateAttendenceStaff(payload);
    setSubmitting(false);
    setSelectedWeek(null);
    setSubmittedRefresh((n) => n + 1);
    swal("Resubmitted", "Your weekly time card was sent to your administrator for approval.", "success");
  };

  // Refresh after a single-entry edit (re-fetch submitted weeks + time card).
  const afterEntrySaved = () => { setSubmittedRefresh((n) => n + 1); load(); };

  // my assigned shifts this week (exclude open) — drives the Schedule tab + counts
  const myWk = useMemo(
    () =>
      (shifts || [])
        .filter((s) => s.status !== "open")
        .map((s) => ({ ...s, _date: startOfDay(new Date(s.date)) }))
        .sort((a, b) => a._date - b._date || a.startMin - b.startMin),
    [shifts]
  );

  // actual clock-in/out records — drives the Time Card (data only exists once a
  // staff member has clocked in/out).
  const records = useMemo(() => {
    const arr = attendanceStaff?.attendanceStaff || (Array.isArray(attendanceStaff) ? attendanceStaff : []);
    return [...arr].sort((a, b) => new Date(a.startTime || a.date?.date || 0) - new Date(b.startTime || b.date?.date || 0));
  }, [attendanceStaff]);

  const scheduledHrs = useMemo(
    () => myWk.reduce((a, s) => a + (s.allDay ? 0 : hrsBetween(s.startMin, s.endMin)), 0),
    [myWk]
  );
  const loggedHrs = useMemo(() => records.reduce((a, t) => a + (Number(t.total_work_time) || 0) / 3600, 0), [records]);
  // Submitted entries (anything the staff has sent for approval — not a draft).
  const submittedRecords = useMemo(() => records.filter((t) => t.submitted !== false), [records]);
  const missing = useMemo(() => {
    const recDays = new Set(records.map((t) => (t.date?.date ? moment(t.date.date).format("YYYY-MM-DD") : t.startTime ? moment(t.startTime).format("YYYY-MM-DD") : "")));
    return myWk.filter((s) => !recDays.has(moment(s.date).format("YYYY-MM-DD"))).length;
  }, [records, myWk]);
  const shiftCount = myWk.length;
  const weekLbl = weekLabel(weekStart);

  const openDetail = async (s) => {
    setHover(null);
    const full = await schedulerService.getShift(idOf(s));
    setDetail(full || s);
  };
  const removeRecord = async (t) => { await deleteAttendanceStaff(idOf(t)); load(); };

  const toggleSel = (id) =>
    setSel((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const allSel = records.length > 0 && records.every((t) => sel.has(idOf(t)));
  const toggleAll = () => setSel(allSel ? new Set() : new Set(records.map((t) => idOf(t))));
  const submittableSelCount = records.filter((t) => sel.has(idOf(t)) && isSubmittable(t)).length;

  // Submit selected draft/rejected records to the admin for approval.
  const submitSelected = async () => {
    const chosen = records.filter((t) => sel.has(idOf(t)) && isSubmittable(t));
    if (!chosen.length) return;
    setSubmitting(true);
    await updateAttendenceStaff(chosen.map((t) => ({ id: idOf(t), submitted: true, approvedStatus: "pending" })));
    setSubmitting(false);
    setSel(new Set());
    await load();
    swal("Submitted for approval", `${chosen.length} time ${chosen.length === 1 ? "entry was" : "entries were"} sent to your administrator.`, "success");
  };

  // Submit a single draft/rejected record for approval (from the detail drawer).
  const submitOne = async (rec) => {
    if (!rec) return;
    setSubmitting(true);
    await updateAttendenceStaff([{ id: idOf(rec), submitted: true, approvedStatus: "pending" }]);
    setSubmitting(false);
    setDetailRecord(null);
    await load();
    swal("Submitted for approval", "Your time entry was sent to your administrator.", "success");
  };

  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const now = new Date();

  const dateNav = (
    <div className={css.dateNav}>
      <button type="button" className={css.navArrow} onClick={() => setWeekStart(addDays(weekStart, -7))} aria-label="Previous"><Icon name="chevronLeft" size={15} /></button>
      <Popover opened={datePop} onChange={setDatePop} position="bottom" withinPortal shadow="md" radius={16}>
        <Popover.Target>
          <button type="button" className={css.dateLabel2} style={{ minWidth: 120 }} onClick={() => setDatePop((o) => !o)} aria-haspopup="dialog">{weekLbl}</button>
        </Popover.Target>
        <Popover.Dropdown p={0}>
          <WeekPicker monthDate={dpMonth} selWeek={startOfWeek(weekStart)} mode="week" onMonthChange={setDpMonth} onPick={goToDate} onClose={() => setDatePop(false)} />
        </Popover.Dropdown>
      </Popover>
      <button type="button" className={css.navArrow} onClick={() => setWeekStart(addDays(weekStart, 7))} aria-label="Next"><Icon name="chevron" size={15} /></button>
      <button type="button" className={`${css.navArrow} ${css.navToday}`} onClick={() => setDatePop((o) => !o)} aria-label="Pick a date" title="Pick a date"><Icon name="calendarDot" size={16} /></button>
    </div>
  );

  const BADGE = {
    draft: { tone: "grey", label: "Not submitted" },
    approved: { tone: "green", label: "Approved" },
    pending: { tone: "amber", label: "Pending" },
    rejected: { tone: "red", label: "Rejected" },
    scheduled: { tone: "blue", label: "Scheduled" },
    inprogress: { tone: "green", label: "In Progress" },
    completed: { tone: "grey", label: "Completed" },
    exception: { tone: "red", label: "Exception" },
    open: { tone: "amber", label: "Open" },
  };
  const StatusBadge = ({ status }) => {
    const b = BADGE[status] || BADGE.pending;
    return <span className={`${css.sBadge} ${css[b.tone]}`}>{b.label}</span>;
  };

  // Segmented [Submitted | Time Off] tabs — rendered inside each sub-view's card header.
  const staffTabs = (
    <div style={{ display: "flex", gap: 6, background: "var(--app-surface-2)", padding: 4, borderRadius: 8, width: "fit-content" }}>
      {[["submitted", "Submitted"], ["timeoff", "Time Off"]].map(([k, l]) => (
        <button key={k} type="button" onClick={() => setSubTab(k)} style={{ padding: "6px 14px", borderRadius: 6, border: 0, cursor: "pointer", fontSize: 13, fontWeight: 600, fontFamily: "inherit", color: "var(--app-text)", background: subTab === k ? "var(--app-surface)" : "transparent", boxShadow: subTab === k ? "0 1px 2px rgba(16,24,40,0.06)" : "none" }}>{l}</button>
      ))}
    </div>
  );

  return (
    <Box style={{ flex: 1, minWidth: 0, maxWidth: "100%", minHeight: "100vh", background: "var(--app-bg)" }}>
      <AppNavbar title="Time Management" profile={profile} />
      <Box pt={24} px={20} pb={20}>
        {/* KPI tiles — Time Card / Schedule / Submitted tabs */}
        <div className={css.kpiGrid}>
          <div
            className={`${css.kpi} ${css.kpiClick} ${tab === "timecard" ? css.kpiActive : ""}`}
            role="button"
            tabIndex={0}
            onClick={() => setTab("timecard")}
          >
            <div className={css.kpiRow}>
              <span className={css.kpiLabel}>{tab === "timecard" ? "Time Card ▾" : "Time Card"}</span>
              <span className={`${css.kpiIcon} ${css.green}`}><Icon name="clock" size={17} /></span>
            </div>
            <div className={css.kpiValue}>{loggedHrs.toFixed(1)}<span className={css.kpiUnit}>hrs</span></div>
            <div className={css.kpiMeta}>{missing ? <><strong>{missing}</strong> missed clock-in{missing === 1 ? "" : "s"}</> : "all clock-ins recorded"}</div>
          </div>

          <div
            className={`${css.kpi} ${css.kpiClick} ${tab === "schedule" ? css.kpiActive : ""}`}
            role="button"
            tabIndex={0}
            onClick={() => setTab("schedule")}
          >
            <div className={css.kpiRow}>
              <span className={css.kpiLabel}>{tab === "schedule" ? "Schedule ▾" : "Schedule"}</span>
              <span className={`${css.kpiIcon} ${css.info}`}><Icon name="calendar" size={17} /></span>
            </div>
            <div className={css.kpiValue}>{shiftCount}<span className={css.kpiUnit}>shifts</span></div>
          </div>

          <div
            className={`${css.kpi} ${css.kpiClick} ${tab === "submitted" ? css.kpiActive : ""}`}
            role="button"
            tabIndex={0}
            onClick={() => { setSubTab("submitted"); setTab("submitted"); }}
          >
            <div className={css.kpiRow}>
              <span className={css.kpiLabel}>{tab === "submitted" ? "Submitted ▾" : "Submitted"}</span>
              <span className={`${css.kpiIcon} ${css.violet}`}><Icon name="send" size={16} /></span>
            </div>
            <div className={css.kpiValue}>{submittedRecords.length}</div>
            <div className={css.kpiMeta}><strong>{timeOffPending}</strong> Time-off request{timeOffPending === 1 ? "" : "s"}</div>
          </div>
        </div>

        {loading ? (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", minHeight: 320, padding: 60 }}>
            <Loader color="var(--app-primary)" />
          </div>
        ) : tab === "timecard" ? (
          /* ---------------- Time Card ---------------- */
          <div className={css.tblCard} style={{ minHeight: "calc(100vh - 240px)", display: "flex", flexDirection: "column" }}>
            <div className={css.tblHead}>
              {dateNav}
              <div className={css.spacer} style={{ flex: 1 }} />
              <span className={css.numStrong} style={{ fontSize: 13, color: "var(--app-text-muted)", whiteSpace: "nowrap" }}>{loggedHrs.toFixed(1)}h logged</span>
              {submittableSelCount > 0 && (
                <button type="button" className={`${css.tbBtn} ${css.tbBtnPrimary}`} style={{ height: 36 }} onClick={submitSelected} disabled={submitting}>
                  <Icon name="send" size={15} /> Submit ({submittableSelCount})
                </button>
              )}
              {allowManualEntry && (
                <button type="button" className={css.tbBtn} style={{ height: 36 }} onClick={openAdd}>
                  <Icon name="plus" size={15} /> Add Missed Time
                </button>
              )}
            </div>
            <div className={css.tcSummary}>
              <div>
                <div className={css.tcBig}>{loggedHrs.toFixed(1)}h</div>
                <div className={css.tcLbl}>logged {weekLbl.toLowerCase()}</div>
              </div>
              {missing ? (
                <div>
                  <div className={`${css.tcBig} ${css.danger}`}>{missing}</div>
                  <div className={css.tcLbl}>missed clock-in{missing === 1 ? "" : "s"}</div>
                </div>
              ) : null}
              {records.length > 0 && (
                <div style={{ marginLeft: "auto", alignSelf: "center", display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, color: "var(--app-text-muted)" }}>
                  <Icon name="check" size={15} />
                  {submittableSelCount > 0 ? `${submittableSelCount} selected · tap Submit to send for approval` : "Select unsubmitted entries to send them for approval"}
                </div>
              )}
            </div>
            <div className={css.tblScroll} style={{ flex: 1 }}>
              <table className={css.tbl}>
                <thead>
                  <tr>
                    <th className={css.ckCol}>
                      <button type="button" className={`${css.ckbox} ${allSel ? css.on : ""}`} onClick={toggleAll} aria-label="Select all">
                        {allSel ? <Icon name="check" size={12} /> : null}
                      </button>
                    </th>
                    <th>Date</th><th>In</th><th>Out</th><th>Hours</th><th>Status</th><th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {records.length === 0 ? (
                    <tr><td colSpan={7} style={{ textAlign: "center", color: "var(--app-text-muted)", padding: 24 }}>No clock-in/out records yet. Hours appear here as you clock in and out.</td></tr>
                  ) : (
                    records.map((t) => {
                      const rid = idOf(t);
                      const d = t.date?.date ? new Date(t.date.date) : t.startTime ? new Date(t.startTime) : null;
                      const isClient = !!t.client;
                      const inT = t.startTime ? moment(t.startTime).format("h:mm A") : null;
                      const outT = t.endTime ? moment(t.endTime).format("h:mm A") : null;
                      const hrs = t.total_work_time ? (Number(t.total_work_time) / 3600).toFixed(1) : null;
                      return (
                        <tr key={rid} className={css.click} onClick={() => setDetailRecord(t)}>
                          <td className={css.ckCol} onClick={(e) => e.stopPropagation()}>
                            <button type="button" className={`${css.ckbox} ${sel.has(rid) ? css.on : ""}`} onClick={() => toggleSel(rid)} aria-label="Select">
                              {sel.has(rid) ? <Icon name="check" size={12} /> : null}
                            </button>
                          </td>
                          <td>
                            <div className={css.numStrong}>{d ? `${DOW[d.getDay()]} ${MON_SHORT[d.getMonth()]} ${d.getDate()}` : "—"}</div>
                            <div style={{ fontSize: 11.5, color: "var(--app-text-muted)" }}>{isClient ? "Client visit" : "Facility shift"}</div>
                          </td>
                          <td>{inT || "—"}</td>
                          <td>{outT || <span style={{ color: "#b45309" }}>Open</span>}</td>
                          <td className={css.numStrong}>{hrs ? `${hrs}h` : "—"}</td>
                          <td><StatusBadge status={tcStatus(t)} /></td>
                          <td style={{ textAlign: "right" }} onClick={(e) => e.stopPropagation()}>
                            <Menu
                              shadow="md"
                              width={140}
                              position="bottom-end"
                              withinPortal
                              radius={10}
                              styles={{
                                dropdown: { padding: 5, border: "1px solid var(--app-border)", boxShadow: "0 8px 24px -6px rgba(16,24,40,0.16)" },
                                item: { fontSize: 13, fontWeight: 500, padding: "8px 11px", borderRadius: 7 },
                              }}
                            >
                              <Menu.Target>
                                <button type="button" className={css.kebabBtn} aria-label="Actions">
                                  <Icon name="dotsV" size={16} />
                                </button>
                              </Menu.Target>
                              <Menu.Dropdown>
                                <Menu.Item onClick={() => setDetailRecord(t)}>View</Menu.Item>
                                <Menu.Item color="red" onClick={() => removeRecord(t)}>Delete</Menu.Item>
                              </Menu.Dropdown>
                            </Menu>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : tab === "submitted" ? (
          /* ---------------- Submitted + Time Off (tabs in card header) ---------------- */
          subTab === "timeoff" ? (
            <TimeOffPanel mode="staff" tabs={staffTabs} onCountChange={setTimeOffPending} onRequest={() => openRequest()} reloadSignal={reqReload} />
          ) : (
            <div className={css.tblCard} style={{ minHeight: "calc(100vh - 240px)", display: "flex", flexDirection: "column" }}>
              <div className={css.tblHead}>
                {staffTabs}
                <div className={css.spacer} style={{ flex: 1 }} />
                <span className={css.numStrong} style={{ fontSize: 13, color: "var(--app-text-muted)", whiteSpace: "nowrap" }}>{submittedWeeks.length} week{submittedWeeks.length === 1 ? "" : "s"}</span>
              </div>
              <div className={css.tblScroll} style={{ flex: 1 }}>
                {submittedLoading ? (
                  <Group position="center" p={48}><Loader color="var(--app-primary)" /></Group>
                ) : submittedWeeks.length === 0 ? (
                  <div style={{ padding: "44px 20px", textAlign: "center", color: "var(--app-text-muted)", fontSize: 14 }}>No submitted timesheets yet. Submit a week from your Time Card to send it for approval.</div>
                ) : (
                <table className={css.tbl}>
                  <thead>
                    <tr>
                      <th>Week</th><th>Hours</th><th>Entries</th><th>Status</th><th aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {submittedWeeks.map((wk) => {
                      const key = moment(wk.weekStart).format("YYYY-MM-DD");
                      return (
                        <tr key={key} className={css.click} onClick={() => setSelectedWeek(wk)}>
                          <td>
                            <div className={css.numStrong}>Week of {weekLabel(wk.weekStart)}</div>
                            {wk.status === "rejected" && wk.rejectionReason ? (
                              <div style={{ fontSize: 11.5, color: "#b42318" }}>Rejected · tap to review</div>
                            ) : null}
                          </td>
                          <td className={css.numStrong}>{wk.totalHours.toFixed(1)}h</td>
                          <td>{wk.count} {wk.count === 1 ? "entry" : "entries"}</td>
                          <td><StatusBadge status={wk.status} /></td>
                          <td style={{ textAlign: "right" }}>
                            <span style={{ color: "#cbd5e1", display: "inline-flex" }}><Icon name="chevron" size={16} /></span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                )}
              </div>
            </div>
          )
        ) : (
          /* ---------------- Schedule ---------------- */
          <div className={css.tblCard}>
            <div className={css.tblHead}>
              <h3>Assigned Schedule</h3>
              <div className={css.spacer} style={{ flex: 1 }} />
              {dateNav}
              <span className={css.numStrong} style={{ fontSize: 13, color: "var(--app-text-muted)", whiteSpace: "nowrap" }}>{scheduledHrs.toFixed(1)}h scheduled</span>
              <div className={css.seg}>
                <button type="button" className={`${css.segBtn} ${schedView === "list" ? css.segActive : ""}`} onClick={() => setSchedView("list")}>List</button>
                <button type="button" className={`${css.segBtn} ${schedView === "calendar" ? css.segActive : ""}`} onClick={() => setSchedView("calendar")}>Calendar</button>
              </div>
            </div>
            {schedView === "calendar" ? (
              <WeekCalendar
                shifts={shifts}
                timeOff={myTimeOff}
                weekStart={weekStart}
                onShiftClick={openDetail}
                onHover={(s, e) => setHover({ shift: s, x: e.clientX, y: e.clientY })}
                onLeave={() => setHover(null)}
              />
            ) : (
              <div className={css.agenda}>
                {days.map((d) => {
                  const sh = myWk.filter((s) => sameDay(s._date, d));
                  const isToday = sameDay(d, now);
                  return (
                    <div key={d.toISOString()} className={`${css.agendaRow} ${isToday ? css.today : ""}`}>
                      <div>
                        <div className={css.agDow}>{DOW[d.getDay()]}</div>
                        <div className={css.agNum}>{MON_SHORT[d.getMonth()]} {d.getDate()}</div>
                      </div>
                      <div className={css.agShifts}>
                        {sh.length === 0 ? (
                          <div className={css.agOff}>No shift · day off</div>
                        ) : (
                          sh.map((s) => {
                            const meta = entryMeta(s);
                            const isFac = s.type === "facility";
                            const color = meta.cat === "facility" ? "#0d9488" : meta.cat === "open" ? "#ffba45" : "#7c3aed";
                            return (
                              <button key={idOf(s)} type="button" className={css.agShift} onClick={() => openDetail(s)}>
                                <span className={css.agBar} style={{ background: color }} />
                                <span className={css.agIc}><Icon name={isFac ? "hospital" : "person"} size={14} /></span>
                                <span className={css.agTime}>{s.allDay ? "All day" : fmtRange(s.startMin, s.endMin)}</span>
                                <span className={css.agLoc}>
                                  <span className={css.agType}>{isFac ? meta.facility || "Facility shift" : meta.clientName || "Client visit"}</span>
                                  <span className={css.agClient}>{isFac ? "Facility shift" : "Client visit"}</span>
                                </span>
                                <span className={css.agRight}><StatusBadge status={s.status} /></span>
                              </button>
                            );
                          })
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {hover && <ShiftPopup shift={hover.shift} x={hover.x} y={hover.y} />}
        <ShiftDrawer
          opened={!!detail}
          shift={detail}
          onClose={() => setDetail(null)}
          readOnly
          onTimeOffSubmitted={() => { setReqReload((n) => n + 1); setDetail(null); setTab("submitted"); setSubTab("timeoff"); }}
        />
        <RequestTimeOffDrawer
          opened={reqOpen}
          prefillStart={reqPrefill}
          onClose={() => setReqOpen(false)}
          onSubmitted={() => { setReqReload((n) => n + 1); setTab("submitted"); setSubTab("timeoff"); }}
        />
        <StaffTimeEntryDrawer
          opened={addOpen}
          onClose={closeAdd}
          onSaved={load}
          staffId={myId}
          staffName={profile?.user?.name}
          weekLabel={weekLbl}
        />
        <TimeEntryDetailDrawer
          opened={!!detailRecord}
          record={detailRecord}
          onClose={() => setDetailRecord(null)}
          onSubmit={() => submitOne(detailRecord)}
          submitting={submitting}
          facilities={[...new Map(myWk.map((s) => [idOf(s.facility), s.facility])).values()].filter(Boolean)}
          clients={[...new Map(myWk.map((s) => [idOf(s.client), s.client])).values()].filter(Boolean)}
        />

        {/* Submitted-week side panel */}
        <Drawer
          opened={!!selectedWeek}
          onClose={() => setSelectedWeek(null)}
          position="right"
          size={520}
          padding={0}
          withCloseButton={false}
          title={null}
          classNames={{ content: "sdDrawerContent" }}
          styles={{ body: { height: "100%", padding: 0 } }}
          zIndex={1000}
        >
          {selectedWeek ? (
            <div className={css.dwWrap}>
              <div className={css.dwHead}>
                <div>
                  <h3 className={css.dwTitle}>Weekly timesheet</h3>
                  <div className={css.dwSub}>Week of {weekLabel(selectedWeek.weekStart)}</div>
                </div>
                <button type="button" className={css.dwClose} onClick={() => setSelectedWeek(null)} aria-label="Close"><Icon name="x" size={16} /></button>
              </div>

              <div className={css.dwBody}>
                <div className={`${css.tsCard} ${css.tsSummary}`}>
                  <div style={{ flex: 1 }}>
                    <div className={css.tsNm}>{selectedWeek.totalHours.toFixed(1)}h</div>
                    <div className={css.tsRole}>{selectedWeek.count} {selectedWeek.count === 1 ? "entry" : "entries"} submitted</div>
                  </div>
                  <StatusBadge status={selectedWeek.status} />
                </div>

                {selectedWeek.status === "rejected" && selectedWeek.rejectionReason ? (
                  <section className={css.dwSection}>
                    <h5 className={css.dwSectionH} style={{ color: "#b42318" }}>Reason for rejection</h5>
                    <div style={{ display: "flex", gap: 10, alignItems: "flex-start", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, padding: "12px 14px" }}>
                      <span style={{ color: "#b42318", flex: "none", marginTop: 1 }}><Icon name="alert" size={16} /></span>
                      <div style={{ color: "#b42318", fontSize: 13, lineHeight: 1.5 }}>{selectedWeek.rejectionReason}</div>
                    </div>
                    <div style={{ fontSize: 12, color: "var(--app-text-muted)", marginTop: 8 }}>Edit the affected entries, then resubmit the week for approval.</div>
                  </section>
                ) : null}

                <section className={css.dwSection}>
                  <h5 className={css.dwSectionH}>Daily entries</h5>
                  <div className={css.tblScroll}>
                    <table className={css.tbl} style={{ minWidth: 0 }}>
                      <thead>
                        <tr><th>Date</th><th>In</th><th>Out</th><th>Hours</th><th>Status</th><th aria-label="Actions" /></tr>
                      </thead>
                      <tbody>
                        {selectedWeek.records.slice().sort((a, b) => new Date(a.startTime || a.date?.date || 0) - new Date(b.startTime || b.date?.date || 0)).map((t) => {
                          const d = t.date?.date ? new Date(t.date.date) : t.startTime ? new Date(t.startTime) : null;
                          const inT = t.startTime ? moment(t.startTime).format("h:mm A") : null;
                          const outT = t.endTime ? moment(t.endTime).format("h:mm A") : null;
                          const hrs = t.total_work_time ? (Number(t.total_work_time) / 3600).toFixed(1) : null;
                          return (
                            <tr key={idOf(t)}>
                              <td className={css.numStrong}>{d ? `${DOW[d.getDay()]} ${MON_SHORT[d.getMonth()]} ${d.getDate()}` : "—"}</td>
                              <td>{inT || "—"}</td>
                              <td>{outT || <span style={{ color: "#b45309" }}>Open</span>}</td>
                              <td className={css.numStrong}>{hrs ? `${hrs}h` : "—"}</td>
                              <td><StatusBadge status={tcStatus(t)} /></td>
                              <td style={{ textAlign: "right" }}>
                                <Menu
                                  shadow="md"
                                  width={150}
                                  position="bottom-end"
                                  withinPortal
                                  zIndex={530}
                                  radius={10}
                                  styles={{
                                    dropdown: { padding: 5, border: "1px solid var(--app-border)", boxShadow: "0 8px 24px -6px rgba(16,24,40,0.16)" },
                                    item: { fontSize: 13, fontWeight: 500, padding: "8px 11px", borderRadius: 7 },
                                  }}
                                >
                                  <Menu.Target>
                                    <button type="button" className={css.kebabBtn} aria-label="Actions"><Icon name="dotsV" size={16} /></button>
                                  </Menu.Target>
                                  <Menu.Dropdown>
                                    <Menu.Item onClick={() => setEditRecord(t)}>Edit entry</Menu.Item>
                                  </Menu.Dropdown>
                                </Menu>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </section>
              </div>

              {selectedWeek.records.some((t) => t.approvedStatus !== "approved") ? (
                <div className={css.dwFoot}>
                  <span style={{ flex: 1 }} />
                  <button type="button" className={`${css.tbBtn} ${css.tbBtnPrimary}`} style={{ height: 38 }} onClick={() => resubmitWeek(selectedWeek)} disabled={submitting}>
                    <Icon name="send" size={15} /> Resubmit week
                  </button>
                </div>
              ) : null}
            </div>
          ) : null}
        </Drawer>

        <TimeEntryEditDrawer
          opened={!!editRecord}
          record={editRecord}
          onClose={() => setEditRecord(null)}
          onSaved={afterEntrySaved}
        />
      </Box>
    </Box>
  );
}
